import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';

function normalizeApiBaseUrl(value: string): string {
  const base = value.trim().replace(/\/+$/, '');
  return base.endsWith('/api') ? base : `${base}/api`;
}

// The backend mounts all application routes below /api. Accepting a Render
// service root here prevents a missing suffix from turning every request into
// a misleading 404, especially during first-time deployment.
export const BASE_URL = normalizeApiBaseUrl(
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api',
);

class ApiClient {
  private client: AxiosInstance;
  private pending: Map<string, Promise<unknown>> = new Map();
  private refreshPromise: Promise<unknown> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      // Generous default: while a protocol is being decompiled the single
      // backend process can be busy, so ordinary reads may take longer than
      // usual. Genuinely long operations (preview, upload) override this with
      // timeout: 0 at the call site.
      timeout: 300000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send HttpOnly cookies on every request
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        const isAuthEndpoint = originalRequest.url?.includes('/auth/');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;
          // Coalesce concurrent 401 refresh attempts into a single request
          if (!this.refreshPromise) {
            this.refreshPromise = (async () => {
              try {
                const res = await axios.post(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true });
                useAuthStore.getState().setUser(res.data);
              } catch (refreshError) {
                useAuthStore.getState().logout();
                throw refreshError;
              }
            })();
            this.refreshPromise.then(
              () => { this.refreshPromise = null; },
              () => { this.refreshPromise = null; }
            );
          }
          try {
            await this.refreshPromise;
            return this.client(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        // Only retry on GET/HEAD server errors; mutating requests like uploads
        // are not idempotent and retrying can create duplicate jobs/protocols.
        const method = (originalRequest.method || 'GET').toLowerCase();
        const status = error.response?.status;
        if (status && status >= 500 && status < 600 && !isAuthEndpoint && (method === 'get' || method === 'head')) {
          return this.retryWithBackoff(originalRequest);
        }

        return Promise.reject(error);
      }
    );
  }

  private async retryWithBackoff(config: AxiosRequestConfig, attempt = 1): Promise<unknown> {
    const maxRetries = 3;
    if (attempt > maxRetries) {
      return Promise.reject(new Error('Max retries exceeded'));
    }
    const delay = Math.min(1000 * 2 ** attempt, 10000);
    await new Promise((r) => setTimeout(r, delay));
    return this.client.request(config);
  }

  private _dedupKey(url: string, config?: AxiosRequestConfig): string {
    const params = config?.params ? JSON.stringify(config.params) : '';
    return `GET:${url}?${params}`;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const key = this._dedupKey(url, config);
    if (this.pending.has(key)) {
      return this.pending.get(key) as Promise<T>;
    }
    const promise = this.client.get<T>(url, config).then((r) => r.data);
    this.pending.set(key, promise);
    promise.finally(() => this.pending.delete(key));
    return promise;
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  public async uploadFile<T>(
    url: string,
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
    params?: Record<string, string>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    // Append query params to URL
    let uploadUrl = url;
    if (params && Object.keys(params).length > 0) {
      const qs = new URLSearchParams(params).toString();
      uploadUrl = `${url}?${qs}`;
    }
    // Do NOT set Content-Type explicitly; let the browser add the boundary.
    // Uploads are not timed-out client-side; the caller's AbortSignal can cancel.
    const response = await this.client.post<T>(uploadUrl, formData, {
      timeout: 0,
      withCredentials: true,
      signal,
      headers: {
        'Content-Type': undefined, // Let browser set multipart boundary
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
    return response.data;
  }
}

const client = new ApiClient();
export default client;
export const apiClient = client;
